import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  DatePicker, 
  Select, 
  Statistic, 
  Button, 
  Table, 
  Typography, 
  Tabs, 
  Space,
  Tag,
  Tooltip,
  message,
  Progress,
  Alert,
  Empty,
  Spin,
  Radio
} from 'antd';
import { 
  DollarOutlined, 
  ClockCircleOutlined, 
  FileExcelOutlined, 
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DownloadOutlined,
  FilterOutlined,
  RiseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Column, Pie, Line } from '@ant-design/plots';
import dayjs from '../../dayjs';
import { useAppContext } from '../../libs/contextLib';
import useAllCharters from './useAllCharters';
import { Charter } from '../../model/charter';
import { defaultRates } from '../../libs/eaPayments';
import { parseSecondsToDuration } from '../../model/charter';
import Papa from 'papaparse';
import LoadMessage from '../../components/LoadMessage';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const ExecutiveDashboard = () => {
  const { operator } = useAppContext();
  const { allChartersArray: allCharters } = useAllCharters();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [selectedPeriod, setSelectedPeriod] = useState('last30');
  const [chartType, setChartType] = useState('revenue');
  const [exportLoading, setExportLoading] = useState(false);
  
  // State for chart legend visibility
  const [chartLegendState, setChartLegendState] = useState({
    Costs: true,
    Profit: true
  });

  const [chartAttributionMode, setChartAttributionMode] = useState('invoice'); // 'invoice' or 'service'


  // Filter and calculate data based on date range
  const dashboardData = useMemo(() => {
    /*
      BUSINESS RULE: For operational metrics (charter count, driver hours, etc.), use charter.dates (array of service dates) instead of charter.startDate. Attribute counts and hours to each date in charter.dates. For financial metrics, use invoice date for accrual-based reporting, unless the invoice date is missing/invalid, in which case fall back to service dates. Toggle allows switching between modes.
    */
    if (!allCharters || !dateRange) return null;
    
    const [startDate, endDate] = dateRange;
    
    // Filter charters within date range (use charter.dates array)
    const filteredCharters = allCharters.filter(charter => {
      if (charter.type !== 'charter#detail') return false;
      const charterDates = (charter.dates || []).map(d => dayjs(d));
      // Include if ANY service date is within the range
      return charterDates.some(date => date.isBetween(startDate, endDate, 'day', '[]'));
    });

    console.log('ExecutiveDashboard - Debug Info:');
    console.log('Total charters:', allCharters?.length);
    console.log('Filtered charters:', filteredCharters.length);
    console.log('Date range:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'));
    console.log('Financial data includes: bookings with issued invoices only (accrual-based)');
    console.log('Financial data attributed to invoice date');

    // Calculate metrics
    let totalRevenue = 0;
    let totalNetRevenue = 0; // Net revenue after discounts, before GST
    let acceptedQuotes = 0;
    let totalDriverHours = 0;
    let regularHours = 0;
    let overtimeHours = 0;
    let weekendHours = 0;
    let publicHolidayHours = 0;
    let totalCharters = filteredCharters.length;
    let completedCharters = 0;
    let pendingCharters = 0;
    let profitMargin = 0;
    let totalCosts = 0;

    // Pre-populate all days in the date range with zero values
    const dailyData = {};
    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      const dateStr = currentDate.format('YYYY-MM-DD');
      dailyData[dateStr] = {
        date: dateStr,
        revenue: 0,
        charters: 0,
        driverHours: 0,
        driverCost: 0,
        vehicleCost: 0,
        profit: 0
      };
      currentDate = currentDate.add(1, 'day');
    }
    
    const statusData = {};
    const dayTypeData = {
      'Weekday': { hours: 0, cost: 0 },
      'Saturday': { hours: 0, cost: 0 },
      'Sunday': { hours: 0, cost: 0 },
      'Public Holiday': { hours: 0, cost: 0 }
    };

    filteredCharters.forEach((charter, index) => {
      console.log(`Charter ${index + 1}:`, {
        name: charter.name,
        status: charter.status,
        startDate: charter.startDate,
        confirmationDate: charter.confirmationDate,
        quoteDate: charter.quoteDate,
        createdAt: charter.createdAt,
        updatedAt: charter.updatedAt,
        hasQuoteLineItems: charter.quoteLineItems?.length > 0,
        quoteLineItems: charter.quoteLineItems,
        hasDuties: charter.duties?.length > 0,
        duties: charter.duties,
        hasQuote: !!charter.quote,
        quote: charter.quote,
        calculateTotal: typeof charter.calculateTotal === 'function' ? charter.calculateTotal() : 'No calculateTotal method'
      });

      const charterDate = dayjs(charter.startDate).format('YYYY-MM-DD');

      // Count by status
      statusData[charter.status] = (statusData[charter.status] || 0) + 1;
      
      // For operational attribution, use all service dates
      const charterDates = (charter.dates || []).map(d => dayjs(d));
      // Always count charter on each service date for charter count (separate from financial attribution)
      charterDates.forEach(serviceDate => {
        const dateStr = serviceDate.format('YYYY-MM-DD');
        if (dailyData[dateStr]) {
          dailyData[dateStr].charters++;
        }
      });

      // --- Attribution logic for financials and driver hours ---
      // Determine which dates to use for financial attribution
      let financialDates = [];
      let useInvoice = false;
      let invoiceDate = null;
      if (charter.invoiceDetails?.invoiceDate && dayjs(charter.invoiceDetails.invoiceDate).isValid()) {
        invoiceDate = dayjs(charter.invoiceDetails.invoiceDate);
        useInvoice = true;
      } else if (charter.invoiceDate && dayjs(charter.invoiceDate, 'DD/MM/YYYY').isValid()) {
        invoiceDate = dayjs(charter.invoiceDate, 'DD/MM/YYYY');
        useInvoice = true;
      }
      if (chartAttributionMode === 'invoice' && useInvoice) {
        financialDates = [invoiceDate];
      } else {
        // Fallback to service dates
        financialDates = charterDates;
      }
      // --- Financials ---
      if (charter.quoteLineItems && charter.quoteLineItems.length > 0) {
        const charterNetRevenue = charter.calculateTotalAfterDiscount();
        const charterGrossRevenue = charter.calculateTotal();
        acceptedQuotes++;
        totalRevenue += charterNetRevenue;
        totalNetRevenue += charterNetRevenue;
        // Costs
        const charterDriverCosts = charter.quoteLineItems.reduce((sum, item) => sum + (item.details?.driverCost || 0), 0);
        const charterVehicleCosts = charter.quoteLineItems.reduce((sum, item) => sum + (item.details?.vehicleCost || 0), 0);
        const charterCosts = charterDriverCosts + charterVehicleCosts;
        totalCosts += charterCosts;
        // Attribute to each financial date
        financialDates.forEach(financialDate => {
          const dateStr = financialDate.format('YYYY-MM-DD');
          if (!dailyData[dateStr]) {
            dailyData[dateStr] = {
              date: dateStr,
              revenue: 0,
              charters: 0,
              driverHours: 0,
              driverCost: 0,
              vehicleCost: 0,
              profit: 0
            };
          }
          dailyData[dateStr].revenue += charterNetRevenue;
          dailyData[dateStr].driverCost += charterDriverCosts;
          dailyData[dateStr].vehicleCost += charterVehicleCosts;
          dailyData[dateStr].profit += (charterNetRevenue - charterCosts);
        });
      } else if (charter.quote && typeof charter.calculateTotal === 'function') {
        // Legacy quote
        try {
          const charterRevenue = charter.calculateTotal();
          acceptedQuotes++;
          totalRevenue += charterRevenue;
          totalNetRevenue += charterRevenue;
          financialDates.forEach(financialDate => {
            const dateStr = financialDate.format('YYYY-MM-DD');
            if (!dailyData[dateStr]) {
              dailyData[dateStr] = {
                date: dateStr,
                revenue: 0,
                charters: 0,
                driverHours: 0,
                driverCost: 0,
                vehicleCost: 0,
                profit: 0
              };
            }
            dailyData[dateStr].revenue += charterRevenue;
            dailyData[dateStr].profit += charterRevenue;
          });
        } catch (error) {
          console.error(`  ❌ Error calculating legacy quote for ${charter.name}:`, error);
        }
      } else {
        console.log(`  ❌ Charter ${charter.name} is committed but has no quote data - skipping revenue calculation`);
      }
      
      if (charter.status === 'completed') {
        completedCharters++;
      } else if (charter.status === 'new' || charter.status === 'sent') {
        pendingCharters++;
      }

      // --- Driver Hours Attribution ---
      let charterDriverHours = 0;
      if (charter.quoteLineItems && charter.quoteLineItems.length > 0) {
        charter.quoteLineItems.forEach(item => {
          if (item.details && item.details.driverCostByDayType) {
            Object.entries(item.details.driverCostByDayType).forEach(([dayType, data]) => {
              if (data.details && data.details.length > 0) {
                data.details.forEach(detail => {
                  const totalHours = (detail.regularHours || 0) + (detail.tier1OTHours || 0) + (detail.tier2OTHours || 0);
                  charterDriverHours += totalHours;
                  // Attribute to each financial date
                  financialDates.forEach(financialDate => {
                    const dateStr = financialDate.format('YYYY-MM-DD');
                    if (dailyData[dateStr]) {
                      dailyData[dateStr].driverHours += totalHours;
                    }
                  });
                  dayTypeData[dayType].hours += totalHours;
                  dayTypeData[dayType].cost += detail.driverCost || 0;
                  regularHours += detail.regularHours || 0;
                  overtimeHours += (detail.tier1OTHours || 0) + (detail.tier2OTHours || 0);
                  if (dayType === 'Saturday' || dayType === 'Sunday') {
                    weekendHours += totalHours;
                  }
                  if (dayType === 'Public Holiday') {
                    publicHolidayHours += totalHours;
                  }
                });
              }
            });
          }
        });
      }
      // Fallback: duties
      if (charterDriverHours === 0 && charter.duties && charter.duties.length > 0) {
        charter.duties.forEach(duty => {
          if (duty && duty.getShiftTime) {
            const shiftTimeSeconds = duty.getShiftTime();
            const shiftTimeHours = shiftTimeSeconds / 3600;
            charterDriverHours += shiftTimeHours;
            // Attribute to each financial date
            financialDates.forEach(financialDate => {
              const dateStr = financialDate.format('YYYY-MM-DD');
              if (dailyData[dateStr]) {
                dailyData[dateStr].driverHours += shiftTimeHours;
              }
            });
            regularHours += shiftTimeHours;
            // Determine day type based on service date
            duty.dates?.forEach(dutyDate => {
              const dayOfWeek = dayjs(dutyDate).day();
              let dayType = 'Weekday';
              if (dayOfWeek === 0) dayType = 'Sunday';
              else if (dayOfWeek === 6) dayType = 'Saturday';
              dayTypeData[dayType].hours += shiftTimeHours;
              if (dayType === 'Saturday' || dayType === 'Sunday') weekendHours += shiftTimeHours;
            });
          }
        });
      }
      // Fallback: quote
      if (charterDriverHours === 0 && charter.quote) {
        Object.values(charter.quote).forEach(quoteItem => {
          if (quoteItem.duration) {
            const durationHours = quoteItem.duration;
            const days = quoteItem.days || 1;
            const totalHours = durationHours * days;
            charterDriverHours += totalHours;
            financialDates.forEach(financialDate => {
              const dateStr = financialDate.format('YYYY-MM-DD');
              if (dailyData[dateStr]) {
                dailyData[dateStr].driverHours += totalHours;
              }
            });
            regularHours += totalHours;
            // Determine day type based on service date
            charterDates.forEach(serviceDate => {
              const dayOfWeek = serviceDate.day();
              let dayType = 'Weekday';
              if (dayOfWeek === 0) dayType = 'Sunday';
              else if (dayOfWeek === 6) dayType = 'Saturday';
              dayTypeData[dayType].hours += totalHours;
              if (dayType === 'Saturday' || dayType === 'Sunday') weekendHours += totalHours;
            });
          }
        });
      }

      console.log(`  Charter total hours: ${charterDriverHours}`);
    });

    console.log('Final totalDriverHours:', totalDriverHours);

    // Calculate profit margin using net revenue (after discounts, before GST)
    if (totalRevenue > 0) {
      profitMargin = ((totalRevenue - totalCosts) / totalRevenue) * 100;
    }

    // Convert daily data to array and sort by date
    const dailyDataArray = Object.values(dailyData).sort((a, b) => 
      dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
    );

    // Debug: Log final daily data
    console.log('📊 Final Daily Data Summary:');
    console.log('Total Revenue:', totalRevenue);
    console.log('Total Costs:', totalCosts);
    console.log('Profit Margin:', profitMargin);
    console.log('Daily Data Array:', dailyDataArray);
    console.log('Status Data:', statusData);

    // Calculate total driver hours as sum of all hour types
    const calculatedTotalDriverHours = regularHours + overtimeHours + weekendHours + publicHolidayHours;
    
    console.log('Hour types breakdown:');
    console.log('Regular Hours:', regularHours);
    console.log('Overtime Hours:', overtimeHours);
    console.log('Weekend Hours:', weekendHours);
    console.log('Public Holiday Hours:', publicHolidayHours);
    console.log('Original totalDriverHours:', totalDriverHours);
    console.log('Calculated totalDriverHours (sum of components):', calculatedTotalDriverHours);

    return {
      totalRevenue,
      totalNetRevenue,
      acceptedQuotes,
      totalDriverHours: calculatedTotalDriverHours,
      regularHours,
      overtimeHours,
      weekendHours,
      publicHolidayHours,
      totalCharters,
      completedCharters,
      pendingCharters,
      profitMargin,
      totalCosts,
      dailyData: dailyDataArray,
      statusData,
      dayTypeData,
      filteredCharters
    };
  }, [allCharters, dateRange, chartAttributionMode]);

  // Handle date range changes
  const handleDateRangeChange = useCallback((dates) => {
    setDateRange(dates);
    setSelectedPeriod('custom');
  }, []);

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period);
    const today = dayjs();
    let startDate, endDate;

    switch (period) {
      case 'last7':
        startDate = today.subtract(7, 'days');
        endDate = today;
        break;
      case 'last30':
        startDate = today.subtract(30, 'days');
        endDate = today;
        break;
      case 'last90':
        startDate = today.subtract(90, 'days');
        endDate = today;
        break;
      case 'thisMonth':
        startDate = today.startOf('month');
        endDate = today.endOf('month');
        break;
      case 'lastMonth':
        startDate = today.subtract(1, 'month').startOf('month');
        endDate = today.subtract(1, 'month').endOf('month');
        break;
      case 'thisYear':
        startDate = today.startOf('year');
        endDate = today.endOf('year');
        break;
      default:
        return;
    }

    setDateRange([startDate, endDate]);
  }, []);

  // Export to CSV functionality
  const handleExportCSV = useCallback(async () => {
    if (!dashboardData) return;

    setExportLoading(true);
    
    try {
      const exportData = [];
      
      // Executive Summary
      exportData.push(['EXECUTIVE DASHBOARD REPORT']);
      exportData.push(['Generated:', dayjs().format('YYYY-MM-DD HH:mm:ss')]);
      exportData.push(['Period:', `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`]);
      exportData.push(['Operator:', operator?.operatorName || 'N/A']);
      exportData.push(['Financial Data Scope:', 'Bookings with invoice dates OR accepted/allocated/completed status']);
      exportData.push(['Financial Attribution:', 'Data attributed to invoice date (preferred) or service date (fallback)']);
      exportData.push([]);
      
      // Financial Summary
      exportData.push(['FINANCIAL SUMMARY']);
      exportData.push(['Total Revenue (AUD):', dashboardData.totalRevenue.toFixed(2)]);
      exportData.push(['Total Costs (AUD):', dashboardData.totalCosts.toFixed(2)]);
      exportData.push(['Profit Margin (%):', dashboardData.profitMargin.toFixed(2)]);
      exportData.push(['Accepted Quotes:', dashboardData.acceptedQuotes]);
      exportData.push(['Total Charters:', dashboardData.totalCharters]);
      exportData.push([]);
      
      // Driver Hours Summary
      exportData.push(['DRIVER HOURS SUMMARY']);
      exportData.push(['Total Driver Hours:', dashboardData.totalDriverHours.toFixed(2)]);
      exportData.push(['Regular Hours:', dashboardData.regularHours.toFixed(2)]);
      exportData.push(['Overtime Hours:', dashboardData.overtimeHours.toFixed(2)]);
      exportData.push(['Weekend Hours:', dashboardData.weekendHours.toFixed(2)]);
      exportData.push(['Public Holiday Hours:', dashboardData.publicHolidayHours.toFixed(2)]);
      exportData.push([]);
      
      // Daily Breakdown
      exportData.push(['DAILY BREAKDOWN']);
      exportData.push(['Date', 'Revenue (AUD)', 'Total Costs (AUD)', 'Profit (AUD)', 'Charters', 'Driver Hours', 'Driver Cost (AUD)', 'Vehicle Cost (AUD)']);
      dashboardData.dailyData.forEach(day => {
        const totalCosts = day.driverCost + day.vehicleCost;
        exportData.push([
          day.date,
          day.revenue.toFixed(2),
          totalCosts.toFixed(2),
          day.profit.toFixed(2),
          day.charters,
          day.driverHours.toFixed(2),
          day.driverCost.toFixed(2),
          day.vehicleCost.toFixed(2)
        ]);
      });
      exportData.push([]);
      
      // Charter Details
      exportData.push(['CHARTER DETAILS']);
      exportData.push(['Charter ID', 'Name', 'Customer', 'Status', 'Service Dates', 'Revenue (AUD)', 'Driver Hours']);
      dashboardData.filteredCharters.forEach(charter => {
        const serviceDates = (charter.dates || []).map(d => dayjs(d).format('YYYY-MM-DD')).join(', ');
        exportData.push([
          charter.charterId,
          charter.name || 'N/A',
          charter.customer?.name || 'N/A',
          charter.status,
          serviceDates || 'N/A',
          charter.calculateTotal().toFixed(2),
          charter.driverHours || 0
        ]);
      });
      
      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const fileName = `executive-dashboard-${dateRange[0].format('YYYY-MM-DD')}-to-${dateRange[1].format('YYYY-MM-DD')}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      
      message.success('Dashboard data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  }, [dashboardData, dateRange, operator]);

  // Chart configurations
  const revenueAndProfitChartConfig = useMemo(() => {
    // Transform data for stacked column chart
    const chartData = [];
    
    dashboardData?.dailyData?.forEach(day => {
      // Validate date before processing
      if (!day.date || !dayjs(day.date).isValid()) {
        console.warn('Invalid date found in daily data:', day.date, day);
        return; // Skip this entry
      }
      
      const totalCosts = day.driverCost + day.vehicleCost;
      const totalRevenue = day.revenue;
      
      // Add costs as the bottom portion (only if enabled)
      if (chartLegendState.Costs) {
        chartData.push({
          date: day.date,
          type: 'Costs',
          value: totalCosts,
          totalRevenue: totalRevenue
        });
      }
      
      // Add profit as the top portion (only if enabled)
      if (chartLegendState.Profit) {
        chartData.push({
          date: day.date,
          type: 'Profit',
          value: day.profit,
          totalRevenue: totalRevenue
        });
      }
    });

    // Determine colors based on what's enabled
    const getChartColors = () => {
      const colors = [];
      if (chartLegendState.Costs) colors.push('#ff7875'); // Red for costs
      if (chartLegendState.Profit) colors.push('#52c41a'); // Green for profit
      return colors;
    };

    return {
      data: chartData,
      isStack: true,
      xField: 'date',
      yField: 'value',
      seriesField: 'type',
      color: getChartColors(),
      legend: false, // Disable built-in legend
      height: 400,
      columnWidthRatio: 0.8, // Make bars thicker
      maxColumnWidth: 60, // Set maximum column width
      minColumnWidth: 20, // Set minimum column width
      padding: [40, 20, 50, 40], // More top padding for labels
      label: false, // Disable default labels
      annotations: dashboardData?.dailyData?.filter(day => {
        // Validate date first
        if (!day.date || !dayjs(day.date).isValid()) {
          return false;
        }
        const costs = day.driverCost + day.vehicleCost;
        const profit = day.profit;
        const hasVisibleData = (chartLegendState.Costs && costs > 0) || (chartLegendState.Profit && profit > 0);
        return hasVisibleData;
      }).map(day => {
        const costs = day.driverCost + day.vehicleCost;
        const profit = day.profit;
        
        // Calculate the displayed total and position based on what's visible
        let displayedTotal = 0;
        let topValue = 0;
        
        if (chartLegendState.Costs && chartLegendState.Profit) {
          // Both visible - show total revenue
          displayedTotal = day.revenue;
          topValue = day.revenue;
        } else if (chartLegendState.Costs && !chartLegendState.Profit) {
          // Only costs visible
          displayedTotal = costs;
          topValue = costs;
        } else if (!chartLegendState.Costs && chartLegendState.Profit) {
          // Only profit visible
          displayedTotal = profit;
          topValue = profit;
        }
        
        return {
          type: 'text',
          position: [day.date, topValue + (topValue * 0.05)], // Position above the top value
          content: `$${displayedTotal.toFixed(0)}`,
          style: {
            fontSize: 11,
            fontWeight: 'bold',
            fill: '#262626',
            textAlign: 'center',
          },
        };
      }) || [],
      xAxis: {
        type: 'time',
        label: {
          autoHide: false,
          autoRotate: false,
          rotate: 0, // Keep labels horizontal like OTR dashboard
          style: {
            fontSize: 12,
            fill: '#666',
          },
          formatter: (text) => {
            return dayjs(text).format('DD/MM');
          },
        },
        tickCount: Math.min(15, chartData.length / 2), // Dynamic tick count based on data
        line: {
          style: {
            stroke: '#e5e5e5',
          },
        },
        tickLine: false,
      },
      yAxis: {
        label: {
          style: {
            fontSize: 11,
            fill: '#666',
          },
          formatter: (val) => {
            if (val >= 1000) {
              return `$${(val / 1000).toFixed(0)}k`;
            }
            return `$${val}`;
          },
        },
        line: {
          style: {
            stroke: '#e5e5e5',
          },
        },
        tickLine: false,
        grid: {
          line: {
            style: {
              stroke: '#f0f0f0',
              lineWidth: 1,
              lineDash: [3, 3], // Dotted grid lines like OTR
            },
          },
        },
      },
      tooltip: {
        shared: true,
        showCrosshairs: true,
        crosshairs: {
          type: 'x',
        },
        customContent: (title, items) => {
          if (!items || items.length === 0) return '';
          
          const date = dayjs(title).format('ddd, MMM DD, YYYY');
          const costs = items.find(item => item.name === 'Costs')?.value || '$0.00';
          const profit = items.find(item => item.name === 'Profit')?.value || '$0.00';
          const revenue = parseFloat(costs.replace('$', '')) + parseFloat(profit.replace('$', ''));
          
          return `
            <div style="padding: 12px; background: white; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
              <div style="font-weight: bold; margin-bottom: 8px; color: #262626;">${date}</div>
              <div style="margin-bottom: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: #1890ff; margin-right: 6px;"></span>
                <strong>Revenue:</strong> $${revenue.toFixed(2)}
              </div>
              <div style="margin-bottom: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: #ff7875; margin-right: 6px;"></span>
                <strong>Costs:</strong> ${costs}
              </div>
              <div>
                <span style="display: inline-block; width: 10px; height: 10px; background: #52c41a; margin-right: 6px;"></span>
                <strong>Profit:</strong> ${profit}
              </div>
            </div>
          `;
        },
      },
      meta: {
        date: {
          alias: 'Date',
          type: 'time',
        },
        value: {
          alias: 'Amount (AUD)',
          formatter: (val) => `$${val.toFixed(0)}`,
        },
      },
    };
  }, [dashboardData, chartLegendState]);

  const driverHoursChartConfig = useMemo(() => ({
    data: dashboardData?.dailyData?.filter(day => day.date && dayjs(day.date).isValid()) || [],
    xField: 'date',
    yField: 'driverHours',
    color: '#722ed1',
    height: 400,
    columnWidthRatio: 0.8, // Make bars thicker
    maxColumnWidth: 60, // Set maximum column width
    minColumnWidth: 20, // Set minimum column width
    padding: [40, 20, 50, 40], // More top padding for labels
    label: {
      position: 'top',
      offset: 8,
      style: {
        fontSize: 11,
        fontWeight: 'bold',
        fill: '#262626',
      },
      formatter: (datum) => {
        // Only show label if driverHours > 0
        if (datum.driverHours > 0) {
          return `${datum.driverHours.toFixed(1)}h`;
        }
        return '';
      },
    },
    xAxis: {
      type: 'time',
      label: {
        autoHide: false,
        autoRotate: false,
        rotate: 0, // Keep labels horizontal like financial chart
        style: {
          fontSize: 12,
          fill: '#666',
        },
        formatter: (text) => {
          return dayjs(text).format('DD/MM');
        },
      },
      tickCount: Math.min(15, (dashboardData?.dailyData?.length || 0) / 2), // Dynamic tick count based on data
      line: {
        style: {
          stroke: '#e5e5e5',
        },
      },
      tickLine: false,
    },
         yAxis: {
       label: {
         style: {
           fontSize: 11,
           fill: '#666',
         },
         formatter: (val) => {
           const numVal = Number(val);
           if (isNaN(numVal)) return val;
           if (numVal >= 10) {
             return `${numVal.toFixed(0)}h`;
           }
           return `${numVal.toFixed(1)}h`;
         },
       },
      line: {
        style: {
          stroke: '#e5e5e5',
        },
      },
      tickLine: false,
      grid: {
        line: {
          style: {
            stroke: '#f0f0f0',
            lineWidth: 1,
            lineDash: [3, 3], // Dotted grid lines like financial chart
          },
        },
      },
    },
    tooltip: {
      shared: true,
      showCrosshairs: true,
      crosshairs: {
        type: 'x',
      },
      customContent: (title, items) => {
        if (!items || items.length === 0) return '';
        
        const date = dayjs(title).format('ddd, MMM DD, YYYY');
        const hours = items[0]?.value || '0.0';
        
        return `
          <div style="padding: 12px; background: white; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: bold; margin-bottom: 8px; color: #262626;">${date}</div>
            <div>
              <span style="display: inline-block; width: 10px; height: 10px; background: #722ed1; margin-right: 6px;"></span>
              <strong>Driver Hours:</strong> ${parseFloat(hours).toFixed(1)}h
            </div>
          </div>
        `;
      },
    },
    meta: {
      date: {
        alias: 'Date',
        type: 'time',
      },
      driverHours: {
        alias: 'Driver Hours',
        formatter: (val) => `${val.toFixed(1)}h`,
      },
    },
  }), [dashboardData]);

  const dayTypePieConfig = useMemo(() => {
    const pieData = Object.entries(dashboardData?.dayTypeData || {}).map(([dayType, data]) => ({
      type: dayType,
      value: data.hours,
      cost: data.cost
    }));

    return {
      data: pieData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.8,
      label: {
        type: 'outer',
        content: '{name}: {percentage}',
      },
      interactions: [
        {
          type: 'element-active',
        },
      ],
    };
  }, [dashboardData]);

  if (!allCharters) {
    return <LoadMessage message="Loading charter data..." />;
  }

  if (!dashboardData) {
    return <Empty description="No charter data available" />;
  }

  return (
    <div className="executive-dashboard">

      {/* Date Range and Export Controls */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex={1}>
            <Space size="middle">
              <Text strong>Period:</Text>
              <Select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                style={{ width: 120 }}
              >
                <Option value="last7">Last 7 days</Option>
                <Option value="last30">Last 30 days</Option>
                <Option value="last90">Last 90 days</Option>
                <Option value="thisMonth">This month</Option>
                <Option value="lastMonth">Last month</Option>
                <Option value="thisYear">This year</Option>
                <Option value="custom">Custom</Option>
              </Select>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              loading={exportLoading}
            >
              Export CSV
            </Button>
          </Col>
        </Row>
      </Card>
      {/* Attribution Mode Toggle */}
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Radio.Group
          value={chartAttributionMode}
          onChange={e => setChartAttributionMode(e.target.value)}
        >
          <Radio.Button value="invoice">By Invoice Date</Radio.Button>
          <Radio.Button value="service">By Travel Dates</Radio.Button>
        </Radio.Group>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={5}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title="Total Revenue"
              value={dashboardData.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title="Total Profit"
              value={dashboardData.totalRevenue - dashboardData.totalCosts}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ 
                color: (dashboardData.totalRevenue - dashboardData.totalCosts) >= 0 ? '#3f8600' : '#cf1322'
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title="Total Costs"
              value={dashboardData.totalCosts}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title="Accepted Quotes"
              value={dashboardData.acceptedQuotes}
              prefix={<RiseOutlined />}
              suffix={`/ ${dashboardData.totalCharters}`}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title="Total Driver Hours"
              value={dashboardData.totalDriverHours}
              precision={1}
              prefix={<ClockCircleOutlined />}
              suffix="hours"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts and Analysis */}
      <Tabs defaultActiveKey="revenue">
        <TabPane 
          tab={
            <span>
              Financial Reporting 
              <Tooltip 
                title={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>How Financial Reports Work</strong>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>What's Included:</strong>
                    </div>
                    <div style={{ marginLeft: 10, marginBottom: 4 }}>
                      • Only bookings that have been invoiced to customers
                    </div>
                    <div style={{ marginLeft: 10, marginBottom: 8 }}>
                      • Revenue figures <strong>exclude GST</strong> (shows your actual earnings)
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>When Revenue is Recorded:</strong>
                    </div>
                    <div style={{ marginLeft: 10, marginBottom: 4 }}>
                      Revenue appears on the date you issued the invoice, not the service date
                    </div>
                    <div style={{ marginLeft: 10, marginBottom: 8 }}>
                      This matches proper accounting practices for your business
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <strong>Profit Calculation:</strong>
                    </div>
                    <div style={{ marginLeft: 10, marginBottom: 4 }}>
                      Revenue (after discounts) - Driver Costs - Vehicle Costs = Profit
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                      This shows your true operational profit without GST complications, 
                      giving you a clear picture of your business performance.
                    </div>
                  </div>
                }
                placement="bottomLeft"
                overlayStyle={{ maxWidth: 380 }}
              >
                <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
              </Tooltip>
            </span>
          } 
          key="revenue"
        >
          <Row gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
            <Col xs={24} lg={18}>
              <Card 
                title={`Daily Revenue (${chartAttributionMode === 'invoice' ? 'By Invoice Date' : 'By Travel Dates'})`} 
                bordered={false} 
                style={{ height: '100%' }}
                extra={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        cursor: 'pointer',
                        opacity: chartLegendState.Costs ? 1 : 0.4,
                        transition: 'opacity 0.2s'
                      }}
                      onClick={() => setChartLegendState(prev => ({ ...prev, Costs: !prev.Costs }))}
                    >
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: '#ff7875', 
                        borderRadius: '2px' 
                      }}></div>
                      <span style={{ fontSize: '12px', color: '#666' }}>Costs</span>
                    </div>
                    <div 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        cursor: 'pointer',
                        opacity: chartLegendState.Profit ? 1 : 0.4,
                        transition: 'opacity 0.2s'
                      }}
                      onClick={() => setChartLegendState(prev => ({ ...prev, Profit: !prev.Profit }))}
                    >
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: '#52c41a', 
                        borderRadius: '2px' 
                      }}></div>
                      <span style={{ fontSize: '12px', color: '#666' }}>Profit</span>
                    </div>
                  </div>
                }
              >
                {revenueAndProfitChartConfig.data.length > 0 ? (
                  <Column {...revenueAndProfitChartConfig} />
                ) : (
                  <Empty description="No valid financial data to display" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={6}>
              <Card
                title={
                  <Space>
                    <span>Performance Trend</span>
                    <Tooltip title="Shows the trend in financial performance between the first and second half of the selected period. A positive value indicates improvement.">
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </Space>
                }
                style={{ height: '100%' }}
              >
                {(() => {
                  if (!dashboardData?.dailyData || dashboardData.dailyData.length === 0) {
                    return <Empty description="No data available" />;
                  }

                  console.log('Trend Analysis Debug:');
                  console.log('Total days in period:', dashboardData.dailyData.length);
                  console.log('Sample daily data:', dashboardData.dailyData.slice(0, 5).map(d => ({
                    date: d.date,
                    revenue: d.revenue,
                    profit: d.profit,
                    costs: d.driverCost + d.vehicleCost
                  })));
                  
                  // Split data into first and second half (including zero days for true average)
                  const dataLength = dashboardData.dailyData.length;
                  const midPoint = Math.floor(dataLength / 2);
                  
                  const firstHalf = dashboardData.dailyData.slice(0, midPoint);
                  const secondHalf = dashboardData.dailyData.slice(midPoint);

                  console.log('First half period:', firstHalf.length, 'days from', firstHalf[0]?.date, 'to', firstHalf[firstHalf.length - 1]?.date);
                  console.log('Second half period:', secondHalf.length, 'days from', secondHalf[0]?.date, 'to', secondHalf[secondHalf.length - 1]?.date);

                  // Calculate true daily averages including zero days
                  const firstHalfRevenue = firstHalf.reduce((sum, day) => sum + day.revenue, 0) / firstHalf.length;
                  const secondHalfRevenue = secondHalf.reduce((sum, day) => sum + day.revenue, 0) / secondHalf.length;
                  
                  const firstHalfProfit = firstHalf.reduce((sum, day) => sum + day.profit, 0) / firstHalf.length;
                  const secondHalfProfit = secondHalf.reduce((sum, day) => sum + day.profit, 0) / secondHalf.length;

                  const firstHalfCosts = firstHalf.reduce((sum, day) => sum + (day.driverCost + day.vehicleCost), 0) / firstHalf.length;
                  const secondHalfCosts = secondHalf.reduce((sum, day) => sum + (day.driverCost + day.vehicleCost), 0) / secondHalf.length;

                  console.log('True daily averages (including zero days):');
                  console.log('Revenue:', { first: firstHalfRevenue.toFixed(2), second: secondHalfRevenue.toFixed(2) });
                  console.log('Profit:', { first: firstHalfProfit.toFixed(2), second: secondHalfProfit.toFixed(2) });
                  console.log('Costs:', { first: firstHalfCosts.toFixed(2), second: secondHalfCosts.toFixed(2) });

                  const firstHalfMargin = firstHalfRevenue > 0 ? (firstHalfProfit / firstHalfRevenue) * 100 : 0;
                  const secondHalfMargin = secondHalfRevenue > 0 ? (secondHalfProfit / secondHalfRevenue) * 100 : 0;

                  // Calculate trends - handle zero baseline cases properly
                  let revenueTrend, profitTrend, costsTrend;
                  
                  // Revenue trend
                  if (firstHalfRevenue > 0.01) {
                    revenueTrend = ((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100;
                  } else if (secondHalfRevenue > 0.01) {
                    revenueTrend = 999; // Show as "New" growth
                  } else {
                    revenueTrend = 0;
                  }
                  
                  // Profit trend
                  if (Math.abs(firstHalfProfit) > 0.01) {
                    profitTrend = ((secondHalfProfit - firstHalfProfit) / Math.abs(firstHalfProfit)) * 100;
                  } else if (Math.abs(secondHalfProfit) > 0.01) {
                    profitTrend = secondHalfProfit > 0 ? 999 : -999; // Show as "New" growth/loss
                  } else {
                    profitTrend = 0;
                  }
                  
                  // Costs trend
                  if (firstHalfCosts > 0.01) {
                    costsTrend = ((secondHalfCosts - firstHalfCosts) / firstHalfCosts) * 100;
                  } else if (secondHalfCosts > 0.01) {
                    costsTrend = 999; // Show as "New" costs
                  } else {
                    costsTrend = 0;
                  }
                  const marginTrend = secondHalfMargin - firstHalfMargin;

                  const getTrendColor = (trend) => {
                    if (trend > 5) return '#3f8600'; // Green for significant improvement
                    if (trend > 0) return '#52c41a'; // Light green for improvement
                    if (trend > -5) return '#faad14'; // Amber for slight decline
                    return '#cf1322'; // Red for significant decline
                  };

                  const getTrendIcon = (trend) => {
                    if (trend > 0) return '↗';
                    if (trend < 0) return '↘';
                    return '→';
                  };

                                     return (
                     <div>
                       {/* Revenue Trend */}
                       <div style={{ marginBottom: 20, padding: 12, backgroundColor: '#fafafa', borderRadius: 6 }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                           <span style={{ fontSize: 15, fontWeight: 'bold' }}>Revenue</span>
                           <span style={{ fontSize: 18, fontWeight: 'bold', color: getTrendColor(revenueTrend) }}>
                             {revenueTrend === 999 ? '↗ NEW' : `${getTrendIcon(revenueTrend)} ${revenueTrend > 0 ? '+' : ''}${revenueTrend.toFixed(1)}%`}
                           </span>
                         </div>
                         <div style={{ fontSize: 12, color: '#666' }}>
                           ${firstHalfRevenue.toFixed(0)}/day → ${secondHalfRevenue.toFixed(0)}/day avg
                         </div>
                       </div>

                       {/* Profit Trend with Margin Impact */}
                       <div style={{ marginBottom: 20, padding: 12, backgroundColor: '#fafafa', borderRadius: 6 }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                           <span style={{ fontSize: 15, fontWeight: 'bold' }}>Profit</span>
                           <span style={{ fontSize: 18, fontWeight: 'bold', color: getTrendColor(profitTrend) }}>
                             {profitTrend === 999 ? '↗ NEW' : profitTrend === -999 ? '↘ NEW' : `${getTrendIcon(profitTrend)} ${profitTrend > 0 ? '+' : ''}${profitTrend.toFixed(1)}%`}
                           </span>
                         </div>
                         <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                           ${firstHalfProfit.toFixed(0)}/day → ${secondHalfProfit.toFixed(0)}/day avg
                         </div>
                         <div style={{ fontSize: 12 }}>
                           <span style={{ color: '#666' }}>Margin: </span>
                           <span style={{ fontWeight: 'bold' }}>{firstHalfMargin.toFixed(1)}%</span>
                           <span style={{ color: '#666' }}> → </span>
                           <span style={{ fontWeight: 'bold', color: getTrendColor(marginTrend) }}>
                             {secondHalfMargin.toFixed(1)}% ({marginTrend > 0 ? '+' : ''}{marginTrend.toFixed(1)}%)
                           </span>
                         </div>
                       </div>

                       {/* Costs Trend */}
                       <div style={{ marginBottom: 20, padding: 12, backgroundColor: '#fafafa', borderRadius: 6 }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                           <span style={{ fontSize: 15, fontWeight: 'bold' }}>Costs</span>
                           <span style={{ fontSize: 18, fontWeight: 'bold', color: getTrendColor(-costsTrend) }}>
                             {costsTrend === 999 ? '↗ NEW' : `${getTrendIcon(costsTrend)} ${costsTrend > 0 ? '+' : ''}${costsTrend.toFixed(1)}%`}
                           </span>
                         </div>
                         <div style={{ fontSize: 12, color: '#666' }}>
                           ${firstHalfCosts.toFixed(0)}/day → ${secondHalfCosts.toFixed(0)}/day avg
                         </div>
                       </div>

                       {/* Overall Period Summary */}
                       <div style={{ textAlign: 'center', padding: 12, backgroundColor: '#f0f9ff', borderRadius: 6 }}>
                         <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Overall Period Margin</div>
                         <div style={{ 
                           fontSize: 24, 
                           fontWeight: 'bold', 
                           color: dashboardData.profitMargin >= 20 ? '#3f8600' : 
                                  dashboardData.profitMargin >= 10 ? '#faad14' : '#cf1322'
                         }}>
                           {dashboardData.profitMargin.toFixed(1)}%
                         </div>
                       </div>
                     </div>
                   );
                })()}
              </Card>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Daily Financial Breakdown" bordered={false}>
                <Table
                  dataSource={dashboardData.dailyData?.filter(day => day.date && dayjs(day.date).isValid()) || []}
                  columns={[
                    {
                      title: 'Date',
                      dataIndex: 'date',
                      key: 'date',
                      render: (date) => {
                        try {
                          return dayjs(date).format('DD MMM YYYY');
                        } catch (error) {
                          console.warn('Invalid date in table:', date);
                          return 'Invalid Date';
                        }
                      },
                    },
                    {
                      title: 'Revenue',
                      dataIndex: 'revenue',
                      key: 'revenue',
                      render: (value) => `$${value.toFixed(2)}`,
                      align: 'right',
                    },
                    {
                      title: 'Costs',
                      key: 'costs',
                      render: (_, record) => `$${(record.driverCost + record.vehicleCost).toFixed(2)}`,
                      align: 'right',
                    },
                    {
                      title: 'Profit',
                      dataIndex: 'profit',
                      key: 'profit',
                      render: (value) => (
                        <span style={{ color: value >= 0 ? '#3f8600' : '#cf1322' }}>
                          $${value.toFixed(2)}
                        </span>
                      ),
                      align: 'right',
                    },
                    {
                      title: 'Charters',
                      dataIndex: 'charters',
                      key: 'charters',
                      align: 'center',
                    },
                  ]}
                  rowKey="date"
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Driver Hours Reporting" key="hours">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card title="Driver Hours Trend" bordered={false}>
                {driverHoursChartConfig.data.length > 0 ? (
                  <Column {...driverHoursChartConfig} />
                ) : (
                  <Empty description="No valid driver hours data to display" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Hours by Day Type" bordered={false}>
                <Pie {...dayTypePieConfig} />
              </Card>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Regular Hours"
                  value={dashboardData.regularHours}
                  precision={1}
                  suffix="hours"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Overtime Hours"
                  value={dashboardData.overtimeHours}
                  precision={1}
                  suffix="hours"
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Weekend Hours"
                  value={dashboardData.weekendHours}
                  precision={1}
                  suffix="hours"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Holiday Hours"
                  value={dashboardData.publicHolidayHours}
                  precision={1}
                  suffix="hours"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Profitability Analysis" key="profitability">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Revenue vs Costs" bordered={false}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Revenue Breakdown:</Text>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ marginBottom: 4 }}>
                      <Text>Total Revenue: </Text>
                      <Text strong style={{ color: '#3f8600' }}>
                        ${dashboardData.totalRevenue.toFixed(2)}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <Text>Total Costs: </Text>
                      <Text strong style={{ color: '#cf1322' }}>
                        ${dashboardData.totalCosts.toFixed(2)}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <Text>Net Profit: </Text>
                      <Text strong style={{ color: dashboardData.totalRevenue - dashboardData.totalCosts >= 0 ? '#3f8600' : '#cf1322' }}>
                        ${(dashboardData.totalRevenue - dashboardData.totalCosts).toFixed(2)}
                      </Text>
                    </div>
                  </div>
                </div>
                <Progress
                  type="circle"
                  percent={Math.min(100, Math.max(0, dashboardData.profitMargin))}
                  strokeColor={dashboardData.profitMargin >= 20 ? '#3f8600' : 
                             dashboardData.profitMargin >= 10 ? '#faad14' : '#cf1322'}
                  format={(percent) => `${dashboardData.profitMargin.toFixed(1)}%`}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Business Insights" bordered={false}>
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    type={dashboardData.profitMargin >= 20 ? 'success' : 
                          dashboardData.profitMargin >= 10 ? 'warning' : 'error'}
                    message={
                      dashboardData.profitMargin >= 20 ? 'Excellent Profitability' :
                      dashboardData.profitMargin >= 10 ? 'Good Profitability' :
                      'Profitability Needs Attention'
                    }
                    description={
                      dashboardData.profitMargin >= 20 ? 'Your charter business is performing very well with strong profit margins.' :
                      dashboardData.profitMargin >= 10 ? 'Your charter business is profitable but there may be room for improvement.' :
                      'Consider reviewing pricing strategies and operational efficiency to improve profitability.'
                    }
                    showIcon
                  />
                </div>
                <div>
                  <Text strong>Key Metrics:</Text>
                  <div style={{ marginTop: 8 }}>
                    <div>Average Revenue per Charter: ${(dashboardData.totalRevenue / Math.max(1, dashboardData.acceptedQuotes)).toFixed(2)}</div>
                    <div>Average Hours per Charter: {(dashboardData.totalDriverHours / Math.max(1, dashboardData.acceptedQuotes)).toFixed(1)}h</div>
                    <div>Quote Acceptance Rate: {((dashboardData.acceptedQuotes / Math.max(1, dashboardData.totalCharters)) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ExecutiveDashboard; 